import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { CheckboxProps, FormInstance, Form, Tooltip } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { Rule } from 'antd/es/form';
import { QuestionCircleOutlined } from '@ant-design/icons';

import { Card } from '@hdi-seguros/web-components.card';
import {
  CheckboxForm,
  ColSize,
  InputCurrencyForm,
  InputNumberForm,
  RowSize,
} from '../../../../../../../../../shared/components';

import { CoveragePerPlan } from '../../../../../../../../../shared/interfaces';

import { formattedPrice } from '../../../../../../../../../shared/utils';
import { generateInputID } from '../../../../../AP/Steps/StepThree/components/Quotation/utils';
import { QuotationInfo } from '../../../../interfaces';
import { GenericDataFormContext } from '../../../../contexts';

interface CoverageCardProps {
  coverage: CoveragePerPlan;
  quotationInfo: QuotationInfo | undefined;
  activitiesForm: FormInstance<any>;
  activityKey: string;
  coveragesList: Array<CoveragePerPlan>;
  relationWithOtherCoverage?: {
    codigoCoberturaRelacionada: string;
    codigoRiesgo: string;
  };
  sumaAseguradaInputName: string;
  primaInputName: string;
  checkboxName: string;
}

export const CoverageCard: FC<CoverageCardProps> = ({
  coverage,
  activitiesForm,
  activityKey,
  coveragesList,
  quotationInfo,
  relationWithOtherCoverage,
  sumaAseguradaInputName,
  primaInputName,
  checkboxName,
}) => {
  const { descripcionLarga } = coverage;

  const [textSubcategory, setTextSubcategory] = useState<string>('');

  const initialCheckboxStatus: boolean = useMemo(
    () => activitiesForm.getFieldValue(checkboxName),
    [activitiesForm, checkboxName]
  );
  const {
    plansSelected
  } = useContext(GenericDataFormContext);


  const [isCoverageEnable, setIsCoverageEnable] = useState<boolean>(
    coverage.tipoCobertura === 'B'
  );

  const tooltipText = useMemo(
    () =>
      `El valor debe estar entre $${formattedPrice(
        coverage.sumaAseguradaMinima
      )} y $${formattedPrice(coverage.sumaAseguradaMaxima)}.`,
    [coverage]
  );

  const tootltipTextDescriptionCoverage = useMemo(() => {
    const relatedCoverage = coveragesList.find(
      (el) =>
        el.codigo === relationWithOtherCoverage?.codigoCoberturaRelacionada &&
        el.codigoRiesgo === relationWithOtherCoverage.codigoRiesgo
    );
    return `Para seleccionar esta cobertura, primero debe activar la cobertura ${relatedCoverage?.descripcionLarga}`;
  }, [coveragesList, relationWithOtherCoverage]);

  const relatedCoverage = useMemo(
    () =>
      coveragesList.find(
        (el) =>
          el.codigo === relationWithOtherCoverage?.codigoCoberturaRelacionada &&
          el.codigoRiesgo === relationWithOtherCoverage.codigoRiesgo
      ),
    [coveragesList, relationWithOtherCoverage]
  );

  const relatedCoverageCheckboxName = useMemo(
    () => generateInputID(relatedCoverage?.id ?? '', activityKey, 'checkbox'),
    [relatedCoverage, activityKey]
  );

  const relatedCoverageCheckboxValue = Form.useWatch(
    relatedCoverageCheckboxName,
    activitiesForm
  );

  //* Clean suma asegurada and prima field from Coverage ( not checked ) or set value of suma asegurada if coverage have relation with other coverage
  const onClickCheckbox: CheckboxProps['onChange'] = async (
    event: CheckboxChangeEvent
  ) => {
    setIsCoverageEnable(event.target.checked);

    if (event.target.checked) {
      if (relationWithOtherCoverage?.codigoCoberturaRelacionada !== '0') {
        const inputName = generateInputID(relatedCoverage?.id!, activityKey);

        const value = activitiesForm.getFieldValue(inputName);
        if (value) {
          activitiesForm.setFieldValue(
            sumaAseguradaInputName,
            Math.round(
              value * (Number(coverage.porcentajeSumaAsegegurada) / 100)
            )
          );
        }
      }
    } else {
      const dependentCoverages = coveragesList.filter(
        (el) => el.codigoCoberturaRelacionada === coverage.codigo
      );

      dependentCoverages.forEach((dependentCoverage) => {
        const dependentCheckboxName = generateInputID(
          dependentCoverage?.id,
          activityKey,
          'checkbox'
        );
        const dependentSumaAseguradaInputName = generateInputID(
          dependentCoverage?.id,
          activityKey,
          'sumaAsegurada'
        );
        const dependentPrimaInputName = generateInputID(
          dependentCoverage?.id,
          activityKey,
          'prima'
        );

        activitiesForm.setFieldValue(dependentCheckboxName, false);

        activitiesForm.setFieldValue(
          dependentSumaAseguradaInputName,
          undefined
        );
        activitiesForm.setFieldValue(dependentPrimaInputName, undefined);
      });
      setIsCoverageEnable(false);
      try {
        activitiesForm.setFieldValue(sumaAseguradaInputName, undefined);
        activitiesForm.setFieldValue(primaInputName, undefined);
        await activitiesForm.validateFields([sumaAseguradaInputName]);
      } catch (error) {}
    }
  };

  //* Rules for all coverages. Required, max and min values.
  const formItemRules: Array<Rule> = useMemo(() => {
    let rules: Array<Rule> = [];

    if (coverage.tipoCobertura === 'B' || isCoverageEnable) {
      rules.push({ required: true, message: 'Debe ingresar un valor.' });
    }

    if (coverage.codigoCoberturaRelacionada === '0') {
      rules.push({
        max: Number(coverage.sumaAseguradaMaxima),
        min: Number(coverage.sumaAseguradaMinima),
        type: 'number',
        message: tooltipText,
      });
    }

    return rules;
  }, [coverage, tooltipText, isCoverageEnable]);

  const tooltipSubTextCoverage = (coverage: CoveragePerPlan) => {
    if (!coverage.subCategoriaDeTextos || coverage.subCategoriaDeTextos.length === 0) {
    return null;
  }
  const text  = coverage.subCategoriaDeTextos
    .sort((a, b) => Number(a.lineaDeTexto) - Number(b.lineaDeTexto))
    .map(item => item.renglonDeTexto)
    .join('\n');
    return (
      <Tooltip title={text} >
        <span className='subcategory-tooltip-icon' aria-label='Ver texto de subcategoría'>
          <QuestionCircleOutlined />
        </span>
      </Tooltip>
    );
  };


  const descriptionCoverageWithOrWithoutTooltip = () => {
    if (relationWithOtherCoverage?.codigoCoberturaRelacionada !== '0') {
      return (
        <>
          {descripcionLarga}{' '}
          <Tooltip title={tootltipTextDescriptionCoverage}>
            <QuestionCircleOutlined />
          </Tooltip>
        </>
      );
    }
    return descripcionLarga;
  };

  const isCheckboxDisable = useMemo(() => {
    if (coverage.tipoDeCobertura === 'B' || coverage.tipoCobertura === 'B')
      return true;
    if (!relatedCoverage) return false;

    if (relatedCoverageCheckboxValue === true) return false;
    if (relatedCoverageCheckboxValue === false) return true;

    return false;
  }, [coverage, relatedCoverage, relatedCoverageCheckboxValue]);

  useEffect(() => {
    if (coverage.tipoCobertura === 'B' || initialCheckboxStatus) {
      setIsCoverageEnable(true);
    }
  }, [coverage.tipoCobertura, initialCheckboxStatus, setIsCoverageEnable]);

  useEffect(() => {
    if (relatedCoverageCheckboxValue === false) {
      setIsCoverageEnable(false);
    }
  }, [relatedCoverageCheckboxValue]);

  useEffect(() => {
    if(isCoverageEnable) {
      activitiesForm.setFieldValue(checkboxName, isCoverageEnable);
    };
  }, [isCoverageEnable, activitiesForm, checkboxName]);

  return (
    <Card title=''>
      <RowSize rowProps={{ align: 'middle', justify: 'start' }}>
        <ColSize size={100}>
          <CheckboxForm
            classNameCheckbox='coverage-card-checkbox'
            checkboxProps={{
              checked: isCoverageEnable,
              disabled: isCheckboxDisable,
              onChange: onClickCheckbox,
            }}
            formProps={{
              label: '',
              name: checkboxName,
              valuePropName: 'checked',
            }}
          >
            {descriptionCoverageWithOrWithoutTooltip()}
            {tooltipSubTextCoverage(coverage)}
          </CheckboxForm>
        </ColSize>
      </RowSize>

      <RowSize>
        <ColSize size={50}>
          <InputCurrencyForm
            formProps={{
              hasFeedback: isCoverageEnable,
              initialValue:
                isCoverageEnable && Number(coverage.sumaAseguradaPorDefecto)
                  ? Number(coverage.sumaAseguradaPorDefecto)
                  : undefined,
              label: 'Suma asegurada',
              name: sumaAseguradaInputName,
              rules: formItemRules,
              tooltip:
                relationWithOtherCoverage?.codigoCoberturaRelacionada === '0'
                  ? tooltipText
                  : undefined,
            }}
            inputCurrencyProps={{
              disabled: !isCoverageEnable,
              readOnly:
                relationWithOtherCoverage?.codigoCoberturaRelacionada !== '0',
            }}
          />
        </ColSize>

        <ColSize size={50}>
          <InputNumberForm
            formProps={{
              label: 'Prima',
              name: primaInputName,
            }}
            inputNumberProps={{
              controls: false,
              disabled: !isCoverageEnable,
              prefix: '$',
              readOnly: true,
            }}
          />
        </ColSize>
      </RowSize>
    </Card>
  );
};
